import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/styles';

import { translate } from '../../../i18n/I18nProvider';
import styles, { customStyles } from '../styles/numbers';
import flowRight from 'lodash.flowright';
import { connect } from 'react-redux';
import { InputAdornment, RadioGroup, TextField } from '@material-ui/core';
import CustomFormControlLabel from '../../../partials/customFormControlLabel';
import { Search } from '@material-ui/icons';
import clsx from 'clsx';
import { isDefined, doesArrayHasLength } from '../../../utils/isDefined';
import { getData } from '../../../utils/http';
import { URLS } from '../../../consts/endpoints';
import mapNumberToDisplay from '../../../utils/mapNumberToDisplay';
import Pagination from '../../../utils/Pagination';
import { buildSearchFilter } from '../actions/filteringActions';

const PAGE_SIZE = 20;

const AvailableNumbers = ({ classes, values, setFieldValue, clientId }) => {
  const [search, setSearch] = useState('');
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const fetchAvailableNumbers = async (page = 0, searchValue = search) => {
    if (!clientId) return;

    setLoading(true);
    try {
      const filter = buildSearchFilter(searchValue);
      const params = { 'page[size]': PAGE_SIZE, 'page[number]': page };
      if (filter) {
        params.phone_number = filter;
      }
      const response = await getData({
        options: {
          url: `${URLS.QUOTE_CLIENTS}/${clientId}/available_numbers`,
          params
        }
      });

      const count = parseInt(
        response?.headers?.['x-total_count'] ||
          response?.headers?.['x-total-count'] ||
          0,
        10
      );

      const data = response?.data ?? response;
      if (doesArrayHasLength(data)) {
        setAvailableNumbers(data);
        setTotalCount(count > PAGE_SIZE ? count : data.length);
      } else {
        setAvailableNumbers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching available numbers:', error);
      setAvailableNumbers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      setCurrentPage(0);
      fetchAvailableNumbers(0, search);
    }
    // eslint-disable-next-line
  }, [clientId]);

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearch(value);
    const filter = buildSearchFilter(value);
    if (filter || value === '') {
      setCurrentPage(0);
      fetchAvailableNumbers(0, value);
    }
  };

  const handleClearSearch = () => {
    setSearch('');
    setCurrentPage(0);
    fetchAvailableNumbers(0, '');
  };

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    fetchAvailableNumbers(newPage, search);
  };

  return (
    <div className={classes.root}>
      {translate('REQUEST_NEW_USER.NUMBERS_INFO')}

      <TextField
        variant="outlined"
        margin="dense"
        onChange={handleSearchChange}
        value={search}
        style={{ ...customStyles.textfieldRoot, maxWidth: '300px' }}
        className={classes.searchField}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isDefined(search) && search.length > 0 && (
                <i
                  className={clsx('fas fa-times', classes.closeIcon)}
                  onClick={handleClearSearch}
                ></i>
              )}
            </InputAdornment>
          )
        }}
        inputProps={{ autoComplete: 'off', 'data-lpignore': true }}
      />
      <div className={classes.numbersContainer}>
        <RadioGroup
          className={classes.radioButtons}
          value={values.mobile_number}
          onChange={e => setFieldValue('mobile_number', e.target.value)}
        >
          {loading && (
            <div className={classes.noNumbers}>{translate('LOADING')}...</div>
          )}
          {!loading && availableNumbers.length === 0 && (
            <div className={classes.noNumbers}>
              {translate('REQUEST_NEW_USER.NO_NUMBERS')}
            </div>
          )}
          {!loading &&
            availableNumbers.map(num => {
              return (
                <CustomFormControlLabel
                  key={num.number}
                  value={num.number}
                  label={mapNumberToDisplay(num.number)}
                />
              );
            })}
        </RadioGroup>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        loading={loading}
      />
    </div>
  );
};

const mapStatesToProps = ({ requestUserWizard, selectClient }) => {
  return {
    availableNumbers: requestUserWizard.selectOptions.availableNumbersOptions,
    clientId: selectClient.selectedClient.id
  };
};

export default flowRight(
  connect(mapStatesToProps),
  withStyles(styles)
)(AvailableNumbers);
